<template>
    <div class="order-form-container">
        <component
            :is="currentComponent"
            :initial-settings="initialSettings"
            :datepicker-config="datepickerConfig"
            :default-selected-product="selectedProduct"
            :default-selected-option="selectedOption"
            :default-selected-rides="selectedRides"
            :default-valid-to="defaultValidTo"
            :route-toll-product="routeTollProduct"
            :default-right-of-withdrawal="defaultRightOfWithdrawal"
            :products="products"
            :license-plate-templates="licensePlateTemplates"
            :country-code-phones="countryCodePhones"
            :toll-products="tollProducts"
            :current-date="currentDate"
            :toll-products-start-price="tollProductsStartPrice"
            :order-price="orderPrice"
            :service-fee="serviceFee"
            :insurance-fee="insuranceFee"
            :selected-country-default="selectedCountry"
            :pre-fill-from-session="preFillFromSession"
            @paypal-status-change="loadingPayPal = $event"
            @klarna-status-change="loadingKlarna = $event"
            @klarna-data-update="klarnaData = $event"
            @request-sending-change="sendingRequest = $event"
            @price-change="orderPrice = $event"
            @service-fee-change="serviceFee = $event"
            @insurance-fee-change="insuranceFee = $event"
            @paypal-loaded="paypalLoaded"
            @klarna-loaded="klarnaLoaded"
            @product-selected="onProductSelection"
            :auto-scroll-up-to-width="autoScrollUpToWidth"
            :force-auto-scroll-on-product-selection="forceAutoScrollOnProductSelection"
            :em-redirect="this.emRedirect"
            :em-abo="this.emAbo"
            :system-id="this.systemId"
            :session-voucher="this.sessionVoucher"
            :shop-timezone-utc-offset="shopTimezoneUtcOffset"
        ></component>

        <div v-if="loadingPayPal" class="d-flex justify-content-center mt-3">
            <i class="fas fa-circle-notch fa-spin"></i>
        </div>

        <div id="payment-row" class="row">
            <div class="col-12 col-md-8 col-lg-9">
                <div id="paypal-button-container"></div>
            </div>
        </div>

        <modal
            id="shop-maintenance-mode-modal"
            :centered="true"
            :title="$t('frontend.attention')"
            :show="maintenanceModal.show"
            :close="maintenanceModal.close"
            :closeable="maintenanceModal.closeable"
            size="modal-lg"
            @modal-opening="onMaintenanceModalOpening"
            @modal-close="onMaintenanceModalClose"
        >
            <div class="modal-body">
                <div class="alert alert-warning" v-html="$t('shop.maintenanceNotice')"></div>
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="maintenanceModal.close = true"
                    v-html="$t('frontend.ok')"
                ></button>
            </div>
        </modal>
    </div>
</template>

<style lang="scss">
.order-form-container {
    #payment-row.wait-for-redirect {
        position: relative;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 99999;
            content: ' ';

            width: 100%;
            height: 100%;

            background-color: rgba(255, 255, 255, 0.4);
        }
    }

    #paypal-button-container {
        padding-left: 20px;
        padding-right: 20px;
    }
}

body.maintenance-modal-open {
    #shop-maintenance-mode-modal {
        z-index: 999999991;
    }
    .modal-backdrop {
        z-index: 999999990;
    }
}
</style>

<script>
import GiftVoucherContainer from './GiftVoucherContainer.vue';
import ProductCardContainer from './ProductCardContainer.vue';

export default {
    props: [
        'initialSettings',
        'datepickerConfig',
        'selectedProduct',
        'selectedOption',
        'selectedRides',
        'routeTollProduct',
        'products',
        'defaultValidTo',
        'licensePlateTemplates',
        'countryCodePhones',
        'tollProducts',
        'tollProductsStartPrice',
        'currentDate',
        'selectedCountry',
        'preFillFromSession',
        'defaultRightOfWithdrawal',
        'autoScrollUpToWidth',
        'forceAutoScrollOnProductSelection',
        'emRedirect',
        'emAbo',
        'systemId',
        'sessionVoucher',
        'shopTimezoneUtcOffset',
    ],
    data() {
        return {
            loadingPayPal: false,
            loadingKlarna: false,
            sendingRequest: false,
            orderPrice: 0.0,
            serviceFee: 0.0,
            insuranceFee: 0.0,
            klarnaData: null,
            showKlarnaPayButton: false,

            maintenanceModal: {
                show: false,
                close: false,
                closeable: true,
            },
        };
    },

    computed: {
        /**
         * Determine the component based on the selected product.
         */
        currentComponent() {
            switch (this.selectedProduct.productType) {
                case 'gift_voucher':
                    return GiftVoucherContainer;
                default:
                    return ProductCardContainer;
            }
        },

        /**
         * Determine if the maintenance mode of shop is enabled.
         *
         * @returns {boolean}
         * */
        isMaintenanceModeEnabled() {
            return (
                typeof this.initialSettings['shop.maintenance'] === 'boolean' &&
                this.initialSettings['shop.maintenance']
            );
        },
    },

    methods: {
        /**
         * Handle the event callback for an selected product to show the maintenance modal, if required.
         *
         * @returns {void}
         */
        onProductSelection() {
            // In the case that the maintenance mode isn't enabled, we can skip the further
            // checks and updates.
            if (!this.isMaintenanceModeEnabled) {
                return;
            }

            this.maintenanceModal.show = true;
        },

        /**
         * Handle the open callback of maintenance modal.
         *
         * @returns {void}
         * */
        onMaintenanceModalOpening() {
            // In the case that the maintenance mode isn't enabled, we can skip the further
            // checks and updates.
            if (!this.isMaintenanceModeEnabled) {
                return;
            }

            document.body.classList.add('overflow-hidden');
            document.body.classList.add('maintenance-modal-open');
        },

        /**
         * Handle the close callback of maintenance modal.
         *
         * @return {void}
         */
        onMaintenanceModalClose() {
            // In the case that the maintenance mode isn't enabled, we can skip the further
            // checks and updates.
            if (!this.isMaintenanceModeEnabled) {
                return;
            }

            // Redirect the user to our start page.
            window.location.href = window.location.origin + '/' + this.$language;
        },

        getScrollTop(ref) {
            let scrollTop = this.$refs[ref].offsetTop;
            const topNav = document.querySelector('#topnav');
            const headerHeight = topNav !== null ? topNav.getBoundingClientRect().height : 0;

            if (scrollTop > 0 && scrollTop - headerHeight >= 0) {
                scrollTop -= headerHeight;
            }
            return scrollTop;
        },

        paypalLoaded() {
            this.$nextTick(
                function () {
                    window.scrollTo(0, this.getScrollTop('paypalContainer'));
                }.bind(this)
            );
        },

        klarnaLoaded() {
            this.$nextTick(
                function () {
                    this.showKlarnaPayButton = true;
                    window.scrollTo(0, this.getScrollTop('klarnaContainer'));
                }.bind(this)
            );
        },

        klarnaFinalize() {
            const that = this;

            Klarna.Payments.authorize(
                {
                    payment_method_category: 'pay_now',
                },
                function (result) {
                    if (result.approved) {
                        location.href =
                            that.klarnaData.payment.return_url + '?authorization_token=' + result.authorization_token;
                    } else {
                        location.href = that.klarnaData.payment.cancel_url;
                    }
                }
            );
        },
    },

    mounted() {
        this.$nextTick(() => {
            this.$el.parentElement.classList.add('rendered');
        });
    },
};
</script>
